/**
 * A volume, shown.
 *
 * The pane used to say "A volume. Open it in the viewer" — an app with a
 * NiiVue viewer in it declining to show an image. The reason was a wrong
 * assumption on my part: that displaying a volume needed a node in the open
 * case's manifest. `NiiVueCanvas` takes a URL, so any file the asset protocol
 * will serve can be drawn, whatever study it belongs to.
 *
 * One canvas rather than the workspace's four. A preview answers "is this the
 * right file and does it look sane", which one plane and a switcher do; four
 * GL contexts alongside the ones the viewer already holds is a real cost for
 * an answer nobody asked for. The way to the full workspace stays one click
 * away for when the question is a reading question.
 */

import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import { NiiVueCanvas } from "@/viewer/NiiVueCanvas";
import { getInstance } from "@/viewer/nvRegistry";
import { PLANE_LABELS } from "@/viewer/types";
import type { LocationInfo, ViewPlane, ViewerStatus } from "@/viewer/types";

const PLANES: ViewPlane[] = ["axial", "sagittal", "coronal", "render"];

/**
 * Which voxel axis a plane steps along.
 *
 * NiiVue reorients to RAS before drawing, so this is the axis of the canonical
 * volume, not of the file: index 2 is always the one an axial view moves
 * through, whatever order the header stored.
 */
const PLANE_AXIS: Partial<Record<ViewPlane, 0 | 1 | 2>> = {
  sagittal: 0,
  coronal: 1,
  axial: 2,
};

const PREVIEW_ID = "output-preview";

export function VolumePreview({
  url,
  name,
  overlayUrl,
}: {
  url: string;
  name: string;
  /** First checked overlay, if any — NiiVue draws one overlay volume per pane. */
  overlayUrl?: string | null;
}) {
  const [plane, setPlane] = useState<ViewPlane>("axial");
  const [status, setStatus] = useState<{ state: ViewerStatus; message: string | null }>({
    state: "empty",
    message: null,
  });
  const [vox, setVox] = useState<[number, number, number] | null>(null);
  /** Slice counts of the canonical volume, read once it has loaded. */
  const [dims, setDims] = useState<[number, number, number] | null>(null);

  // A different file means different dimensions and a different position.
  useEffect(() => {
    setDims(null);
    setVox(null);
  }, [url]);

  /**
   * Read the shape and the starting position once the volume is in.
   *
   * Both come from NiiVue rather than from React: the reoriented dimensions
   * exist only after loading, and `onLocationChange` fires when the crosshair
   * *moves* — never on arrival — so waiting for it left the slider disabled
   * on every file nobody had clicked in yet.
   */
  useEffect(() => {
    if (status.state !== "ready") return;
    const nv = getInstance(PREVIEW_ID);
    const volume = nv?.volumes?.[0];
    if (!nv || !volume) return;

    const d = volume.dimsRAS ?? volume.dims;
    if (d && d.length >= 4) setDims([d[1], d[2], d[3]]);

    if (!vox && nv.scene?.crosshairPos) {
      try {
        const v = nv.frac2vox(nv.scene.crosshairPos);
        setVox([v[0], v[1], v[2]]);
      } catch {
        // An unusual header can refuse the conversion; the slider then stays
        // disabled rather than moving to a position nobody can trust.
      }
    }
  }, [status.state, vox, url]);

  // `render` and `multiplanar` step through nothing, so they get no slider.
  const axis = PLANE_AXIS[plane] ?? null;
  const max = axis != null && dims ? Math.max(dims[axis] - 1, 0) : 0;
  const at = axis != null && vox ? Math.round(vox[axis]) : 0;

  /** Move to a slice by index, which NiiVue only does relatively. */
  const goTo = (to: number) => {
    if (axis == null || !vox) return;
    const nv = getInstance(PREVIEW_ID);
    if (!nv) return;
    const delta: [number, number, number] = [0, 0, 0];
    delta[axis] = Math.round(to) - Math.round(vox[axis]);
    if (delta[axis] === 0) return;
    nv.moveCrosshairInVox(delta[0], delta[1], delta[2]);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-ads-viewer">
      <div className="relative min-h-0 flex-1">
        <NiiVueCanvas
          // One preview at a time, so one identity; a file change swaps the
          // volume in the same context rather than churning a new one.
          id="output-preview"
          baseUrl={url}
          overlayUrl={overlayUrl ?? null}
          sliceType={plane}
          opacity={overlayUrl ? 0.6 : 1}
          colormap="gray"
          overlayColormap="warm"
          crosshair
          linked={false}
          onLocationChange={(loc: LocationInfo) => setVox(loc.vox)}
          onStatusChange={(state, message) => setStatus({ state, message })}
          className="h-full w-full"
        />
        {status.state === "error" && status.message ? (
          <p className="absolute inset-x-0 bottom-0 bg-red-950/80 px-3 py-2 text-[11px] leading-relaxed text-red-100">
            {status.message}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-col gap-1 border-t border-ads-border bg-ads-surface px-3 py-1.5">
        <div className="flex items-center gap-1">
          {PLANES.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPlane(p)}
              className={cn(
                "rounded px-2 py-0.5 text-[11px] transition",
                p === plane
                  ? "bg-ads-accent-soft font-medium text-ads-accent"
                  : "text-neutral-500 hover:bg-ads-hover",
              )}
            >
              {PLANE_LABELS[p]}
            </button>
          ))}
          <span className="min-w-0 flex-1 truncate text-right text-[10.5px] text-neutral-400">
            {status.state === "loading" ? "Loading…" : name}
          </span>
        </div>

        {/* Stepping by slice number, which scrolling cannot do: "the one below
            the last" and "slice 31 again tomorrow" are both positions, and a
            wheel reaches neither. Hidden for the rendering, which has no
            slices to step through. */}
        {axis != null ? (
          <div className="flex items-center gap-2 pb-0.5">
            <input
              type="range"
              min={0}
              max={max}
              step={1}
              value={Math.min(at, max)}
              disabled={!dims || !vox}
              aria-label={`${PLANE_LABELS[plane]} slice`}
              onChange={(e) => goTo(Number(e.target.value))}
              // Tall enough to hit: a four-pixel track is a target you miss,
              // and missing it means clicking the image and moving the
              // crosshair somewhere you did not mean.
              className="h-4 min-w-0 flex-1 cursor-pointer accent-ads-accent disabled:cursor-default disabled:opacity-40"
            />
            <span className="w-20 shrink-0 text-right text-[10.5px] tabular-nums text-neutral-500">
              {dims && vox ? `${Math.min(at, max) + 1} / ${max + 1}` : "—"}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
